import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useLang } from '../../i18n';
import { LoopVideo } from '../LoopVideo';

/**
 * «Materia»: origine, studio, territorio.
 *
 * Lo sfondo resta fermo a tutta pagina e cambia loop in dissolvenza mentre i
 * capitoli ci scorrono sopra. Si scaricano solo il loop del capitolo attivo e
 * il successivo; gli altri non partono. Con reduced motion: poster fermi e
 * capitoli impilati.
 *
 * I loop sono generati con AI (materia, nessun luogo riconoscibile): A-15.
 */
export const MateriaChapters = ({ capitoli }) => {
  const { t } = useLang();
  const ridotto = useReducedMotion();
  const [attivo, setAttivo] = useState(0);
  const lista = useRef(null);

  useEffect(() => {
    if (ridotto || !lista.current) return undefined;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setAttivo(Number(e.target.dataset.indice));
        }),
      { rootMargin: '-45% 0px -45% 0px' },
    );
    lista.current.querySelectorAll('[data-indice]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ridotto, capitoli.length]);

  const testo = (c) => (
    <>
      <h2 className="mt-4 text-[clamp(2.2rem,5vw,4rem)] leading-[1.02]">{t(`company.${c.key}Title`)}</h2>
      <p className="prosa mt-5 !text-carta/85">{t(`company.${c.key}Text`)}</p>
    </>
  );

  if (ridotto) {
    return (
      <section data-testid="company-materia" className="bg-notte text-carta">
        {capitoli.map((c, i) => (
          <article key={c.key} className="wrap grid gap-8 py-16 md:grid-cols-2 md:items-center md:gap-16">
            <img
              src={c.video.poster}
              alt=""
              aria-hidden="true"
              width={c.video.width}
              height={c.video.height}
              loading="lazy"
              decoding="async"
              className="aspect-video w-full object-cover"
            />
            <div>{testo(c)}</div>
          </article>
        ))}
      </section>
    );
  }

  return (
    <section data-testid="company-materia" className="relative bg-notte text-carta">
      <div data-testid="company-materia-sticky" className="sticky top-0 h-[100svh] overflow-hidden">
        {capitoli.map((c, i) => (
          <LoopVideo
            key={c.key}
            {...c.video}
            carica={i <= attivo + 1}
            attivo={i === attivo}
            pausaClassName="bottom-4 right-4"
            className={`absolute inset-0 transition-opacity duration-1000 ${i === attivo ? 'opacity-100' : 'opacity-0'}`}
          />
        ))}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-notte/40"
        />
      </div>
      <div ref={lista} className="pointer-events-none relative -mt-[100svh]">
        {capitoli.map((c, i) => (
          <article key={c.key} data-indice={i} className="wrap flex min-h-[100svh] items-end pb-16 md:pb-24">
            <div className="pointer-events-auto max-w-xl bg-notte/80 p-7 md:p-10">{testo(c)}</div>
          </article>
        ))}
      </div>
    </section>
  );
};
